import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import Button from '../ui/Button';
import SafeIcon from '../../common/SafeIcon';

const { FiMenu, FiX, FiChevronDown } = FiIcons;
const { FaUtensils, FaSpa, FaUmbrellaBeach, FaConciergeBell, FaBookOpen } = FaIcons;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileDiscoverOpen, setMobileDiscoverOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setMobileDiscoverOpen(false);
    setScrolled(window.scrollY > 20);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Stay', path: '/rooms' },
    {
      name: 'Discover',
      path: null,
      children: [
        { name: 'Dining', path: '/dining', icon: FaUtensils, desc: 'Culinary Delights' },
        { name: 'Wellness', path: '/wellness', icon: FaSpa, desc: 'Spa & Relax' },
        { name: 'Activities', path: '/activities', icon: FaUmbrellaBeach, desc: 'Explore Zanzibar' },
        { name: 'Amenities', path: '/amenities', icon: FaConciergeBell, desc: 'Our Services' },
        { name: 'Blog', path: '/blog', icon: FaBookOpen, desc: 'Stories & News' }
      ]
    },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' },
  ];

  const shouldBeSolid = scrolled || !isHome || isOpen;
  const textColorClass = shouldBeSolid ? 'text-brand-900' : 'text-white drop-shadow-md';
  const subTextClass = shouldBeSolid ? 'text-brand-500' : 'text-brand-100 opacity-90';
  
  // Larger text (text-sm), thinner font (font-medium), refined tracking for main menu
  const navLinkBase = "text-sm font-medium uppercase tracking-widest transition-colors duration-300 flex items-center gap-1 cursor-pointer py-4";
  
  const navLinkClass = shouldBeSolid
    ? `${navLinkBase} text-brand-900 hover:text-brand-500`
    : `${navLinkBase} text-white drop-shadow-md hover:text-brand-200 hover:drop-shadow-none`;

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${shouldBeSolid ? 'bg-white shadow-md py-2' : 'bg-transparent py-5'}`}>
      <div className="container mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="z-50 relative group">
          <div className={`font-serif text-2xl md:text-3xl font-bold tracking-tight leading-none transition-colors duration-300 ${textColorClass}`}>
            Amani Palms
            <span className={`block text-[7px] md:text-[8px] font-sans font-medium tracking-widest uppercase mt-1 md:-mt-3 transition-colors duration-300 ${subTextClass}`}>
              by Manolo Beach Resort
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
          {navLinks.map((link) => (
            <div key={link.name} className="relative group">
              {link.children ? (
                <>
                  {/* Trigger */}
                  <div className={navLinkClass}>
                    {link.name}
                    <SafeIcon icon={FiChevronDown} className="text-sm transition-transform duration-300 group-hover:rotate-180" />
                  </div>
                  
                  {/* Mega Menu Dropdown */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-[600px] bg-white shadow-2xl rounded-xl p-6 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-4 group-hover:translate-y-0 border border-brand-100 before:content-[''] before:absolute before:-top-2 before:left-1/2 before:-translate-x-1/2 before:border-l-[8px] before:border-r-[8px] before:border-b-[8px] before:border-l-transparent before:border-r-transparent before:border-b-white">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                       {/* Map first 4 items normally */}
                       {link.children.slice(0, 4).map((child) => (
                        <Link 
                          key={child.name} 
                          to={child.path} 
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-brand-50 transition-all duration-300 group/item"
                        >
                          <div className="w-10 h-10 rounded-full bg-brand-50 text-brand-500 flex items-center justify-center group-hover/item:bg-white group-hover/item:text-brand-600 group-hover/item:shadow-sm transition-all border border-transparent group-hover/item:border-brand-100">
                            <SafeIcon icon={child.icon} className="text-lg" />
                          </div>
                          <div>
                            {/* UPDATED: text-base (bigger), font-medium (thinner than bold) */}
                            <div className="text-base font-serif font-medium text-brand-900 leading-none mb-1 group-hover/item:text-brand-600 transition-colors">
                              {child.name}
                            </div>
                            {/* UPDATED: text-xs (bigger than 10px), font-light (thinner) */}
                            <div className="text-xs text-brand-400 uppercase tracking-widest font-light">
                              {child.desc}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    {/* Render 5th item (Blog) full width or centered below */}
                    <div className="mt-2 pt-2 border-t border-brand-100">
                        {link.children.slice(4).map((child) => (
                        <Link 
                          key={child.name} 
                          to={child.path} 
                          className="flex items-center gap-4 p-3 rounded-lg hover:bg-brand-50 transition-all duration-300 group/item"
                        >
                          <div className="w-10 h-10 rounded-full bg-brand-50 text-brand-500 flex items-center justify-center group-hover/item:bg-white group-hover/item:text-brand-600 group-hover/item:shadow-sm transition-all border border-transparent group-hover/item:border-brand-100">
                            <SafeIcon icon={child.icon} className="text-lg" />
                          </div>
                          <div>
                            {/* UPDATED: text-base (bigger), font-medium (thinner than bold) */}
                            <div className="text-base font-serif font-medium text-brand-900 leading-none mb-1 group-hover/item:text-brand-600 transition-colors">
                              {child.name}
                            </div>
                            {/* UPDATED: text-xs (bigger than 10px), font-light (thinner) */}
                            <div className="text-xs text-brand-400 uppercase tracking-widest font-light">
                              {child.desc}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <Link to={link.path} className={navLinkClass}>
                  {link.name}
                </Link>
              )}
            </div>
          ))}
          <Button 
            to="/contact" 
            variant={shouldBeSolid ? 'primary' : 'outline'}
            className={`py-2 px-6 text-xs font-medium transition-all duration-300 ${!shouldBeSolid ? 'text-white border-white hover:bg-white hover:text-brand-900' : ''}`}
          >
            Book Your Stay
          </Button>
        </nav>

        {/* Mobile Toggle */}
        <button 
          className="lg:hidden z-50 p-2 focus:outline-none transition-transform active:scale-95" 
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle Menu"
        >
          <SafeIcon icon={isOpen ? FiX : FiMenu} className={`text-2xl transition-colors duration-300 ${textColorClass}`} />
        </button>

        {/* Mobile Menu Overlay */}
        <div className={`fixed inset-0 bg-brand-50 z-40 flex flex-col items-center justify-center transition-all duration-500 lg:hidden overflow-y-auto ${isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-10'}`}>
          <div className="flex flex-col items-center space-y-6 py-20 w-full max-w-sm px-6">
            {navLinks.map((link) => (
              <div key={link.name} className="flex flex-col items-center w-full">
                {link.children ? (
                  <>
                    <button 
                      onClick={() => setMobileDiscoverOpen(!mobileDiscoverOpen)}
                      className="text-3xl font-serif text-brand-900 hover:text-brand-500 transition-colors flex items-center gap-2"
                    >
                      {link.name}
                      <SafeIcon icon={FiChevronDown} className={`text-xl transition-transform duration-300 ${mobileDiscoverOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {/* Mobile Submenu */}
                    <div className={`overflow-hidden transition-all duration-500 ease-in-out w-full ${mobileDiscoverOpen ? 'max-h-[600px] opacity-100 mt-6' : 'max-h-0 opacity-0'}`}>
                      <div className="grid grid-cols-1 gap-3">
                        {link.children.map((child) => (
                          <Link 
                            key={child.name} 
                            to={child.path} 
                            className="flex items-center gap-4 p-3 bg-white rounded-lg shadow-sm border border-brand-100"
                            onClick={() => setIsOpen(false)}
                          >
                            <div className="text-brand-500 bg-brand-50 p-2 rounded-full">
                              <SafeIcon icon={child.icon} className="text-lg" />
                            </div>
                            <div className="text-left">
                              {/* UPDATED: text-xl (bigger), font-medium */}
                              <div className="text-xl font-serif font-medium text-brand-900 leading-none">{child.name}</div>
                              {/* UPDATED: text-xs (bigger), font-light */}
                              <div className="text-xs text-brand-400 uppercase tracking-widest mt-1 font-light">{child.desc}</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link 
                    to={link.path} 
                    className="text-3xl font-serif text-brand-900 hover:text-brand-500 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <Button to="/contact" className="mt-8 scale-110 w-full justify-center" onClick={() => setIsOpen(false)}>
              Book Your Stay
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;